import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LogOut, MessageCircle, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { AiStatus, StatusBadge } from "@/components/product";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/features/auth/useAuth";
import { useActiveEvent } from "@/features/event/useActiveEvent";
import { ThemeToggle } from "@/features/theme/ThemeToggle";
import { logout } from "@/services/api/auth";
import { shortenEventName } from "./shorten-event-name";

/**
 * Topbar do painel — compacta, informação útil sem competir com o
 * conteúdo: evento ativo, acesso contextual à PoC de WhatsApp (o item saiu
 * da sidebar, ver `router/nav-config.ts`), status da IA, tema, identidade
 * do usuário.
 */
export function Topbar({ onOpenNav }: { onOpenNav: () => void }) {
  const { me } = useAuth();
  const { event } = useActiveEvent();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });

  // Não invalidamos queryKeys.auth.me manualmente: o cache inteiro já foi
  // limpo acima e a navegação pra /login desmonta o que dependia dele.

  const displayName = (me?.usuario ?? "").trim();
  const initials = displayName.slice(0, 2).toUpperCase() || "··";

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="lg:hidden"
        aria-label="Abrir navegação"
        onClick={onOpenNav}
      >
        <Menu className="size-5" />
      </Button>

      {event ? (
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate text-sm font-semibold text-fg" title={event.name}>
            {shortenEventName(event.name)}
          </span>
          <span className="hidden shrink-0 sm:inline-flex">
            <StatusBadge tone="accent">Evento ativo</StatusBadge>
          </span>
        </div>
      ) : null}

      <span className="grow" />

      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        title="Simular no WhatsApp"
        aria-label="Simular no WhatsApp"
        onClick={() => window.open("/ui/poc-whatsapp", "_blank", "noopener")}
      >
        <MessageCircle className="size-4" aria-hidden="true" />
      </Button>

      {/* Estado real da IA não é exposto por nenhum endpoint hoje — `state`
          fica no default ("active"), reproduzindo o comportamento anterior
          (pill sempre "IA no ar"). Ver AiStatus (components/product) e o
          Known gap correspondente no relatório do Shell. */}
      <AiStatus className="hidden sm:inline-flex" />

      <ThemeToggle />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon-sm"
            className="rounded-full"
            aria-label="Menu do usuário"
          >
            <span className="text-xs font-bold">{initials}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="truncate">
            {displayName || "Usuário"}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            disabled={logoutMutation.isPending}
            onClick={() => logoutMutation.mutate()}
          >
            <LogOut className="size-4" aria-hidden="true" />
            Sair da conta
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
